// NEW_FILE_JUSTIFIED: Enrichment diff view — field-by-field comparison for policy enrichment
import { cn } from '@/lib/utils';
import { Plus, AlertTriangle } from 'lucide-react';
import type { IEnrichmentDiffViewProps, IEnrichmentDiff } from '@/@types/bulkImport';

export function EnrichmentDiffView({ diffs }: IEnrichmentDiffViewProps) {
  const significantDiffs = diffs.filter(d => d.type !== 'match');

  if (significantDiffs.length === 0) {
    return <p className="text-xs text-muted-foreground italic py-2">No differences found</p>;
  }

  return (
    <div className="rounded-md border border-border overflow-hidden">
      <div className="grid grid-cols-[120px_1fr_1fr] text-xs font-medium bg-muted/50 px-3 py-1.5">
        <span className="text-muted-foreground">Field</span>
        <span className="text-muted-foreground">Current</span>
        <span className="text-muted-foreground">New Value</span>
      </div>
      {significantDiffs.map((diff, index) => (
        <DiffRow key={`${diff.field}-${index}`} diff={diff} />
      ))}
    </div>
  );
}

function DiffRow({ diff }: { diff: IEnrichmentDiff }) {
  const icon =
    diff.type === 'new_data' ? (
      <Plus className="w-3 h-3 text-emerald-500" aria-hidden="true" />
    ) : (
      <AlertTriangle className="w-3 h-3 text-amber-500" aria-hidden="true" />
    );

  return (
    <div
      className={cn(
        'grid grid-cols-[120px_1fr_1fr] text-xs px-3 py-2 border-t border-border',
        diff.type === 'new_data' && 'bg-emerald-500/5',
        diff.type === 'conflict' && 'bg-amber-500/5'
      )}
    >
      <div className="flex items-center gap-1.5">
        {icon}
        <span className="text-foreground font-medium">{diff.label}</span>
      </div>
      <span className="text-muted-foreground truncate">{diff.existingValue || '—'}</span>
      <span
        className={cn(
          'truncate',
          diff.type === 'new_data' && 'text-emerald-600 dark:text-emerald-400',
          diff.type === 'conflict' && 'text-amber-600 dark:text-amber-400'
        )}
      >
        {diff.newValue || '—'}
      </span>
    </div>
  );
}
